import * as THREE from 'three';
import type { PoiConfig } from '../../poiConfig';
import type { Sight, SightId } from '../../types';
import type { ICollisionSystem } from '../collision/colliderTypes';
import { computeClusters, type ClusterGroup, type ClusterMarkerData, type MarkerAABB } from './PoiClusterEngine';
import poiMoveToIconSvg from '../../icons/poi-move-to.svg?raw';
import { isInsideScene } from '../insideScenes/registry';

const hasMoveToInsideScene = (sight: Sight): boolean =>
  typeof sight.moveToSceneId === 'string' && isInsideScene(sight.moveToSceneId);

export interface PoiMarkerDiagnostics {
  sightId: SightId;
  distance: number;
  visible: boolean;
  occluded: boolean;
}

interface PoiMarker {
  element: HTMLButtonElement;
  position: THREE.Vector3;
  lastX: number;
  lastY: number;
  hidden: boolean;
  isFocused: boolean;
  isRight: boolean;
  sightId: SightId;
  distance: number;
  occluded: boolean;
  clustered: boolean;
  labelWidth: number;
}

export interface ClusterZoomTarget {
  x: number;
  y: number;
  z: number;
}

interface PoiMarkerManagerOptions {
  container: HTMLElement;
  gestureTarget: HTMLElement;
  collisionSystem?: ICollisionSystem | null;
  onNavigateToSight: (sightId: SightId) => void;
  onClusterZoom?: (centroid: ClusterZoomTarget) => void;
  initialConfig: PoiConfig;
}

const POI_MARKER_HIDDEN_CLASS = 'is-hidden';
const POI_MARKER_OCCLUDED_CLASS = 'is-occluded';
const POI_MARKER_FOCUSED_CLASS = 'is-focused';
const POI_MARKER_CLUSTERED_CLASS = 'is-clustered';
const POI_MARKER_RIGHT_CLASS = 'is-right';
// The 3D point sits at the marker CENTER.
const POI_MARKER_CENTERING_TRANSFORM = ' translate(-50%, -50%)';
const POI_GESTURE_DRAG_THRESHOLD_PX = 5;
const MAX_CLUSTER_POOL_SIZE = 8;

// AABB constants derived from CSS (.scene-poi-marker, .scene-poi-marker__label)
const BUTTON_HALF = 10; // 20px button / 2
const LABEL_GAP = 4; // margin: -14px 4px → horizontal gap
const LABEL_OFFSET_Y = -14; // margin-top
const LABEL_HEIGHT = 26; // 14px * 1.2 line-height + 2*4px padding + ~1px border

export class PoiMarkerManager {
  private readonly poiLayer: HTMLDivElement;
  private readonly collisionSystem: ICollisionSystem | null;
  private readonly onNavigateToSight: (sightId: SightId) => void;
  private readonly onClusterZoom: ((centroid: ClusterZoomTarget) => void) | null;
  private readonly gestureTarget: HTMLElement;
  private markers: PoiMarker[] = [];
  private cameraDirection = new THREE.Vector3();
  private markerVector = new THREE.Vector3();
  private markerToCamera = new THREE.Vector3();
  private lineOfSightTarget = new THREE.Vector3();
  private markerDirectionToCamera = new THREE.Vector3();
  private config: PoiConfig;
  private focusedSightId: SightId | null = null;
  /** Markers held off the layer while their subject is elsewhere — see
   *  `$suppressedSightIds` in `state/appState.ts`. */
  private suppressedSightIds: ReadonlySet<SightId> = new Set();
  private readonly markerLookup = new Map<HTMLButtonElement, PoiMarker>();
  private readonly pointerStates = new Map<
    number,
    {
      startX: number;
      startY: number;
      moved: boolean;
      element: HTMLButtonElement;
      deferredDownInit: PointerEventInit;
    }
  >();
  private readonly suppressClickElements = new WeakSet<HTMLButtonElement>();

  // Cluster state
  private clusterPool: HTMLButtonElement[] = [];
  private clusterCountSpans: HTMLSpanElement[] = [];
  private clusterPoolGroupIndex: number[] = [];
  private prevClusterIdByMarker: (number | null)[] = [];
  private clusterMarkerData: ClusterMarkerData[] = [];

  constructor(options: PoiMarkerManagerOptions) {
    this.collisionSystem = options.collisionSystem ?? null;
    this.onNavigateToSight = options.onNavigateToSight;
    this.onClusterZoom = options.onClusterZoom ?? null;
    this.gestureTarget = options.gestureTarget;
    this.config = options.initialConfig;

    this.poiLayer = document.createElement('div');
    this.poiLayer.className = 'scene-poi-layer';
    this.poiLayer.dir = 'ltr';
    options.container.appendChild(this.poiLayer);
  }

  setConfig(config: PoiConfig): void {
    this.config = config;
  }

  setFocusedSightId(sightId: SightId | null): void {
    this.focusedSightId = sightId;
  }

  /**
   * Hides these markers outright — no label, no dot, out of clustering — until
   * called again with a set that omits them. Used while the crow is still
   * airborne after its opening tour was skipped: its POI would otherwise sit on
   * an empty perch. The scene layer is driven directly rather than by filtering
   * the `sights` array, because that array is a `SceneCanvas` effect dependency
   * and changing it re-creates the scene.
   */
  setSuppressedSightIds(sightIds: ReadonlySet<SightId>): void {
    this.suppressedSightIds = sightIds;
  }

  createMarkers(sights: Sight[]): void {
    if (sights.length === 0) {
      return;
    }
    sights.forEach((sight) => {
      const position = sight.position;
      if (!position) {
        return;
      }
      const markerElement = document.createElement('button');
      markerElement.type = 'button';
      markerElement.className = `scene-poi-marker ${POI_MARKER_HIDDEN_CLASS}`;
      markerElement.dataset.sightId = sight.id.toString();
      markerElement.addEventListener('pointerdown', this.handleMarkerPointerDown);
      markerElement.addEventListener('pointermove', this.handleMarkerPointerMove);
      markerElement.addEventListener('pointerup', this.handleMarkerPointerUp);
      markerElement.addEventListener('pointercancel', this.handleMarkerPointerCancel);
      markerElement.addEventListener('click', this.handleMarkerClick);

      const dot = document.createElement('span');
      dot.className = 'scene-poi-marker__dot';
      dot.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.className = 'scene-poi-marker__label';
      label.dir = document.documentElement.dir;
      const labelText = document.createElement('span');
      labelText.className = 'scene-poi-marker__label-text';
      labelText.textContent = sight.name ?? sight.id.toString();
      label.appendChild(labelText);
      if (hasMoveToInsideScene(sight)) {
        label.classList.add('has-icon');
        const icon = document.createElement('span');
        icon.className = 'scene-poi-marker__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = poiMoveToIconSvg;
        label.appendChild(icon);
      }
      markerElement.append(dot, label);
      const labelOffsetWidth = label.offsetWidth;

      this.poiLayer.appendChild(markerElement);
      const marker: PoiMarker = {
        element: markerElement,
        position: new THREE.Vector3(position.x, position.y, position.z),
        lastX: Number.NaN,
        lastY: Number.NaN,
        hidden: true,
        isFocused: false,
        isRight: false,
        sightId: sight.id,
        distance: Number.POSITIVE_INFINITY,
        occluded: false,
        clustered: false,
        labelWidth: labelOffsetWidth,
      };
      this.markers.push(marker);
      this.markerLookup.set(markerElement, marker);
    });

    this.createClusterPool();
  }

  // ── Pointer event handlers (markers) ──────────────────────────────

  private handleMarkerPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }
    const element = event.currentTarget as HTMLButtonElement | null;
    if (!element) {
      return;
    }
    this.pointerStates.set(event.pointerId, {
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      element,
      deferredDownInit: this.buildForwardEventInit(event),
    });
    if (element.setPointerCapture) {
      try {
        element.setPointerCapture(event.pointerId);
      } catch {
        // Ignore capture failures.
      }
    }
  };

  private handleMarkerPointerMove = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    if (state.moved) {
      this.forwardPointerEvent('pointermove', event);
      return;
    }
    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    if (Math.hypot(deltaX, deltaY) < POI_GESTURE_DRAG_THRESHOLD_PX) {
      return;
    }
    state.moved = true;
    this.suppressClickElements.add(state.element);
    try {
      state.element.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may have already been released.
    }
    this.gestureTarget.dispatchEvent(
      new PointerEvent('pointerdown', state.deferredDownInit),
    );
    this.forwardPointerEvent('pointermove', event);
  };

  private handleMarkerPointerUp = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    this.pointerStates.delete(event.pointerId);
    try {
      state.element.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may have already been released.
    }
    this.suppressClickElements.add(state.element);
    if (state.moved) {
      this.forwardPointerEvent('pointerup', event);
    } else {
      const marker = this.markerLookup.get(state.element);
      if (marker) {
        this.onNavigateToSight(marker.sightId);
      }
    }
  };

  private handleMarkerPointerCancel = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    this.pointerStates.delete(event.pointerId);
    this.suppressClickElements.add(state.element);
    if (state.moved) {
      this.forwardPointerEvent('pointercancel', event);
    }
  };

  private handleMarkerClick = (event: MouseEvent): void => {
    const element = event.currentTarget as HTMLButtonElement | null;
    if (!element) {
      return;
    }
    if (this.suppressClickElements.has(element)) {
      this.suppressClickElements.delete(element);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const marker = this.markerLookup.get(element);
    if (marker) {
      this.onNavigateToSight(marker.sightId);
    }
  };

  // ── Cluster pool & event handlers ─────────────────────────────────

  private createClusterPool(): void {
    const poolSize = Math.min(
      Math.floor(this.markers.length / 2),
      MAX_CLUSTER_POOL_SIZE,
    );
    for (let i = 0; i < poolSize; i += 1) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'scene-poi-cluster is-hidden';
      el.addEventListener('pointerdown', this.handleMarkerPointerDown);
      el.addEventListener('pointermove', this.handleMarkerPointerMove);
      el.addEventListener('pointerup', this.handleClusterPointerUp);
      el.addEventListener('pointercancel', this.handleMarkerPointerCancel);
      el.addEventListener('click', this.handleClusterClick);
      const count = document.createElement('span');
      count.className = 'scene-poi-cluster__count';
      el.append(count);
      this.poiLayer.appendChild(el);
      this.clusterPool.push(el);
      this.clusterCountSpans.push(count);
      this.clusterPoolGroupIndex.push(-1);
    }
  }

  private handleClusterPointerUp = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    this.pointerStates.delete(event.pointerId);
    try {
      state.element.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may have already been released.
    }
    if (state.moved) {
      this.suppressClickElements.add(state.element);
      this.forwardPointerEvent('pointerup', event);
    }
    // For taps (not moved): let the click event fire naturally
  };

  private handleClusterClick = (event: MouseEvent): void => {
    const element = event.currentTarget as HTMLButtonElement | null;
    if (!element) {
      return;
    }
    if (this.suppressClickElements.has(element)) {
      this.suppressClickElements.delete(element);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const poolIndex = this.clusterPool.indexOf(element);
    if (poolIndex === -1) return;
    const groupIndex = this.clusterPoolGroupIndex[poolIndex];
    if (groupIndex === -1) return;
    this.zoomToCluster(groupIndex);
  };

  private zoomToCluster(groupIndex: number): void {
    if (!this.onClusterZoom) return;

    // Collect cluster members
    const memberIndices: number[] = [];
    for (let i = 0; i < this.prevClusterIdByMarker.length; i += 1) {
      if (this.prevClusterIdByMarker[i] === groupIndex) {
        memberIndices.push(i);
      }
    }
    if (memberIndices.length === 0) return;

    // Compute screen centroid of the cluster
    let avgSX = 0;
    let avgSY = 0;
    for (const idx of memberIndices) {
      avgSX += this.markers[idx].lastX;
      avgSY += this.markers[idx].lastY;
    }
    avgSX /= memberIndices.length;
    avgSY /= memberIndices.length;

    // Pick the member closest to the screen centroid — a real surface point
    // that is visually "in the middle" of the cluster
    let bestIdx = memberIndices[0];
    let bestDistSq = Number.POSITIVE_INFINITY;
    for (const idx of memberIndices) {
      const dx = this.markers[idx].lastX - avgSX;
      const dy = this.markers[idx].lastY - avgSY;
      const dSq = dx * dx + dy * dy;
      if (dSq < bestDistSq) {
        bestDistSq = dSq;
        bestIdx = idx;
      }
    }

    const target = this.markers[bestIdx].position;
    this.onClusterZoom({ x: target.x, y: target.y, z: target.z });
  }

  // ── Shared helpers ────────────────────────────────────────────────

  private buildForwardEventInit(event: PointerEvent): PointerEventInit {
    return {
      bubbles: true,
      cancelable: true,
      composed: true,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      isPrimary: event.isPrimary,
      clientX: event.clientX,
      clientY: event.clientY,
      screenX: event.screenX,
      screenY: event.screenY,
      button: event.button,
      buttons: event.buttons,
      pressure: event.pressure,
      width: event.width,
      height: event.height,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
    };
  }

  private forwardPointerEvent(type: string, event: PointerEvent): void {
    this.gestureTarget.dispatchEvent(
      new PointerEvent(type, this.buildForwardEventInit(event)),
    );
  }

  // ── AABB computation ─────────────────────────────────────────────

  private computeMarkerAABB(marker: PoiMarker): MarkerAABB {
    const cx = marker.lastX;
    const cy = marker.lastY;

    // (cx, cy) is the CENTER. Button area + label sideways.
    let minX = cx - BUTTON_HALF;
    let maxX = cx + BUTTON_HALF;
    const minY = cy + LABEL_OFFSET_Y; // cy - 14 (label extends above button)
    const maxY = cy + LABEL_OFFSET_Y + LABEL_HEIGHT; // cy + 12

    // Label area
    if (marker.isRight) {
      // Label to the left of button
      minX = Math.min(minX, cx - BUTTON_HALF - LABEL_GAP - marker.labelWidth);
    } else {
      // Label to the right of button
      maxX = Math.max(maxX, cx + BUTTON_HALF + LABEL_GAP + marker.labelWidth);
    }

    return { minX, minY, maxX, maxY };
  }

  // ── Per-frame update ──────────────────────────────────────────────

  update(camera: THREE.PerspectiveCamera, canvasWidth: number, canvasHeight: number): void {
    const markers = this.markers;
    const markerCount = markers.length;
    if (markerCount === 0) {
      return;
    }

    // ── Phase 1: Projection & per-marker state ──
    this.cameraDirection.set(0, 0, -1).applyQuaternion(camera.quaternion).normalize();
    const widthHalf = canvasWidth * 0.5;
    const heightHalf = canvasHeight * 0.5;
    const cameraPosition = camera.position;
    const probeMaxOffset = Math.max(0, this.config.occlusionProbeMaxOffset);
    const probeMinDistance = Math.max(0, this.config.occlusionProbeMinDistance);

    // Ensure clusterMarkerData array is the right size
    while (this.clusterMarkerData.length < markerCount) {
      this.clusterMarkerData.push({ screenX: 0, screenY: 0, aabb: { minX: 0, minY: 0, maxX: 0, maxY: 0 }, eligible: false });
    }

    for (let i = 0; i < markerCount; i += 1) {
      const marker = markers[i];
      const { position, element } = marker;
      this.markerVector.copy(position).project(camera);
      this.markerToCamera.subVectors(position, cameraPosition);
      const markerDistance = this.markerToCamera.length();

      const clipX = this.markerVector.x;
      const clipY = this.markerVector.y;
      const clipZ = this.markerVector.z;
      const isBehindCamera = this.markerToCamera.dot(this.cameraDirection) <= 0;
      const outsideFrustum =
        clipX < -1 ||
        clipX > 1 ||
        clipY < -1 ||
        clipY > 1 ||
        clipZ < -1 ||
        clipZ > 1;
      const shouldHide =
        isBehindCamera || outsideFrustum || this.suppressedSightIds.has(marker.sightId);
      let occludedByCollision = false;

      if (!shouldHide && this.collisionSystem) {
        this.lineOfSightTarget.copy(position);
        if (markerDistance > probeMinDistance) {
          const maxProbe = markerDistance - probeMinDistance;
          const probeDistance = Math.min(maxProbe, probeMaxOffset);
          if (probeDistance > 0) {
            this.markerDirectionToCamera.copy(this.markerToCamera).normalize();
            this.lineOfSightTarget.addScaledVector(this.markerDirectionToCamera, -probeDistance);
          }
        }
        const occluded = this.collisionSystem.isLineOfSightObstructed(cameraPosition, this.lineOfSightTarget);
        occludedByCollision = occluded;
      }

      if (marker.occluded !== occludedByCollision) {
        marker.occluded = occludedByCollision;
        element.classList.toggle(POI_MARKER_OCCLUDED_CLASS, occludedByCollision);
      }
      marker.distance = markerDistance;

      const isFocused = this.focusedSightId !== null && marker.sightId === this.focusedSightId;
      if (marker.isFocused !== isFocused) {
        marker.isFocused = isFocused;
        element.classList.toggle(POI_MARKER_FOCUSED_CLASS, isFocused);
      }

      if (shouldHide) {
        if (!marker.hidden) {
          marker.hidden = true;
          element.classList.add(POI_MARKER_HIDDEN_CLASS);
        }
      } else {
        if (marker.hidden) {
          marker.hidden = false;
          element.classList.remove(POI_MARKER_HIDDEN_CLASS);
        }
      }

      // Compute screen coords for all visible markers (needed for clustering)
      const rawScreenX = clipX * widthHalf + widthHalf;
      const rawScreenY = -clipY * heightHalf + heightHalf;
      const screenX = +rawScreenX.toFixed(2);
      const screenY = +rawScreenY.toFixed(2);
      marker.lastX = screenX;
      marker.lastY = screenY;

      // Build data for clustering
      const sd = this.clusterMarkerData[i];
      sd.screenX = screenX;
      sd.screenY = screenY;
      sd.eligible = !shouldHide && !occludedByCollision && !isFocused;
      if (sd.eligible) {
        sd.aabb = this.computeMarkerAABB(marker);
      }
    }

    // ── Phase 2: Clustering ──
    const clusteringEnabled = this.config.clusterEnabled && this.clusterPool.length > 0;
    let activeGroups: ClusterGroup[] = [];
    let clusterIdByMarker: (number | null)[] = this.prevClusterIdByMarker;

    if (clusteringEnabled) {
      const result = computeClusters(
        this.clusterMarkerData,
        this.prevClusterIdByMarker,
        this.config.clusterUnmergePadding,
      );
      activeGroups = result.groups;
      clusterIdByMarker = result.clusterIdByMarker;
      this.prevClusterIdByMarker = clusterIdByMarker;
    }

    // ── Phase 3: DOM updates ──
    for (let i = 0; i < markerCount; i += 1) {
      const marker = markers[i];
      const { element } = marker;

      if (marker.hidden) {
        // Already hidden by Phase 1 — ensure clustered class is removed
        if (marker.clustered) {
          marker.clustered = false;
          element.classList.remove(POI_MARKER_CLUSTERED_CLASS);
        }
        continue;
      }

      const isClustered = clusteringEnabled && clusterIdByMarker[i] != null;

      if (isClustered) {
        // Hide individual marker (it's represented by a cluster)
        if (!marker.clustered) {
          marker.clustered = true;
          element.classList.add(POI_MARKER_CLUSTERED_CLASS);
        }
      } else {
        // Show individual marker
        if (marker.clustered) {
          marker.clustered = false;
          element.classList.remove(POI_MARKER_CLUSTERED_CLASS);
        }
        element.style.transform = `translate3d(${marker.lastX}px, ${marker.lastY}px, 0)${POI_MARKER_CENTERING_TRANSFORM}`;
      }
    }

    // Position cluster pool elements
    const poolSize = this.clusterPool.length;
    const groupCount = Math.min(activeGroups.length, poolSize);

    for (let p = 0; p < poolSize; p += 1) {
      const el = this.clusterPool[p];
      if (p < groupCount) {
        const group = activeGroups[p];
        const cx = +group.centroidX.toFixed(2);
        const cy = +group.centroidY.toFixed(2);
        el.style.transform = `translate3d(${cx}px, ${cy}px, 0)${POI_MARKER_CENTERING_TRANSFORM}`;
        this.clusterCountSpans[p].textContent = String(group.memberIndices.length);
        this.clusterPoolGroupIndex[p] = p;
        if (el.classList.contains('is-hidden')) {
          el.classList.remove('is-hidden');
        }
      } else {
        // Hide unused pool element
        this.clusterPoolGroupIndex[p] = -1;
        if (!el.classList.contains('is-hidden')) {
          el.classList.add('is-hidden');
        }
      }
    }
  }

  getDiagnostics(): PoiMarkerDiagnostics[] {
    if (this.markers.length === 0) {
      return [];
    }
    return this.markers.map((marker) => ({
      sightId: marker.sightId,
      distance: marker.distance,
      visible: !marker.hidden,
      occluded: marker.occluded,
    }));
  }

  setVisible(visible: boolean): void {
    this.poiLayer.style.display = visible ? '' : 'none';
  }

  dispose(): void {
    this.markers.splice(0).forEach((marker) => {
      marker.element.remove();
    });
    this.clusterPool.forEach((el) => el.remove());
    this.clusterPool = [];
    this.clusterCountSpans = [];
    this.clusterPoolGroupIndex = [];
    this.poiLayer.remove();
  }
}
